import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProfile } from '../contexts/ProfileContext';
import QRScanner from './QRScanner';
import { FiEdit, FiShare2, FiUpload, FiPlus, FiEye, FiSettings, FiCamera } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';

const QuickActions = ({ profile }) => {
  const { publishProfile, publishing } = useProfile();
  const [showQRScanner, setShowQRScanner] = useState(false);

  const handlePublishToIPFS = async () => {
    const result = await publishProfile();
    if (result.success) {
      alert(`Profile published to IPFS! CID: ${result.cid}`);
    } else {
      alert(`Failed to publish: ${result.error}`);
    }
  };

  const handleShareProfile = async () => {
    const url = `${window.location.origin}/#/u/${profile.username}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile.display_name || profile.username}'s HubID Profile`,
          text: 'Check out my Web3 identity on HubID',
          url: url,
        });
      } catch (error) {
        // Fallback to clipboard
        navigator.clipboard.writeText(url);
        alert('Profile link copied to clipboard!');
      }
    } else {
      navigator.clipboard.writeText(url);
      alert('Profile link copied to clipboard!');
    }
  };

  const handleScanQR = () => {
    setShowQRScanner(true);
  };

  const actions = [
    { name: 'Edit Profile', description: 'Update your profile information', icon: FiEdit, href: '/profile' },
    { name: 'View Profile', description: 'See your public profile page', icon: FiEye, href: `/u/${profile.username}`, external: true },
    { name: 'Scan QR Code', description: 'Scan QR codes to connect', icon: FiCamera, onClick: handleScanQR },
    { name: 'Publish to IPFS', description: 'Export your profile to IPFS', icon: FiUpload, onClick: handlePublishToIPFS, loading: publishing },
    { name: 'Share Profile', description: 'Share your profile link', icon: FiShare2, onClick: handleShareProfile },
    { name: 'Add Content', description: 'Add new blocks to your profile', icon: FiPlus, href: '/profile?tab=blocks' },
    { name: 'Settings', description: 'Manage your account settings', icon: FiSettings, href: '/settings' },
  ];

  return (
    <>
      {showQRScanner && (
        <QRScanner
          onClose={() => setShowQRScanner(false)}
          onScan={(text) => {
            console.log('Scanned:', text);
            setShowQRScanner(false);
          }}
        />
      )}
      <div className="glass-dark rounded-xl border border-dark-700 p-6">
        <h2 className="text-lg font-semibold text-lime-bright mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {actions.map((action, index) => {
            const ActionComponent = action.href ? Link : 'button';
            const props = action.href
              ? { to: action.href, ...(action.external ? { target: '_blank', rel: 'noopener noreferrer' } : {}) }
              : { onClick: action.onClick, disabled: action.loading };

            return (
              <ActionComponent
                key={index}
                {...props}
                className="flex items-start p-4 rounded-lg glass border border-dark-700 hover:border-lime-500/50 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-10 h-10 bg-lime-500/20 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 border border-lime-500/50">
                  <SafeIcon icon={action.icon} className="text-lime-400 text-lg" />
                </div>
                <div>
                  <h3 className="font-medium text-dark-100 mb-1">
                    {action.loading ? 'Publishing...' : action.name}
                  </h3>
                  <p className="text-sm text-dark-400">
                    {action.description}
                  </p>
                </div>
              </ActionComponent>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default QuickActions;